import client from '@/graphql/client';
import {
  CreateOrganizationDocument,
  CreateOrganizationAddressDocument,
  type CreateOrganizationInput,
  type CreateOrganizationAddressInput,
} from '@/generated/root-types';
import { getAllSetupData, clearSetupData } from '../utils/setupStorage';

export interface ExecuteSetupProgress {
  progress: number;
  step: string;
}

interface OrganizationDraft {
  name: string;
  description?: string;
}

interface AddressDraft {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  email?: string;
  phoneNumber?: string;
}

const buildAddressInput = (
  organizationId: string,
  type: CreateOrganizationAddressInput['type'],
  address: AddressDraft,
): CreateOrganizationAddressInput => ({
  organizationId,
  type,
  isDefault: true,
  addressLine1: address.addressLine1 || undefined,
  addressLine2: address.addressLine2 || undefined,
  zipCode: address.zipCode || undefined,
  city: address.city || undefined,
  country: address.country || undefined,
  email: address.email || undefined,
  phoneNumber: address.phoneNumber || undefined,
});

/**
 * Runs the reseller setup pipeline: create the reseller's own organization, then
 * its headquarters address, then its customer-contact address (used when
 * inviting customers). Mirrors backoffice's `executeSetup` batched-localStorage pattern.
 */
export const executeSetup = async (
  onProgress: (progress: number, step: string) => void,
): Promise<{ organizationId: string }> => {
  const setupData = getAllSetupData();

  if (!setupData.organization) {
    throw new Error('Organization data is missing');
  }
  if (!setupData.organizationAddress) {
    throw new Error('Organization address data is missing');
  }
  if (!setupData.customerContact) {
    throw new Error('Customer contact address data is missing');
  }

  const organization = setupData.organization as OrganizationDraft;
  const headquartersAddress = setupData.organizationAddress as AddressDraft;
  const customerContactAddress = setupData.customerContact as AddressDraft;

  try {
    onProgress(0, 'CreatingOrganization');
    const orgInput: CreateOrganizationInput = {
      name: organization.name,
      description: organization.description || undefined,
    };

    const orgResult = await client.mutate({
      mutation: CreateOrganizationDocument,
      variables: { input: orgInput },
    });

    const newOrg = orgResult.data?.createOrganization?.organization;
    if (!newOrg?.id) {
      throw new Error('Failed to create organization');
    }
    const organizationId = newOrg.id;

    onProgress(35, 'CreatingAddress');
    const headquartersResult = await client.mutate({
      mutation: CreateOrganizationAddressDocument,
      variables: {
        input: buildAddressInput(organizationId, 'HEADQUARTERS', headquartersAddress),
      },
    });

    if (!headquartersResult.data?.createOrganizationAddress?.organizationAddress?.id) {
      throw new Error('Failed to create headquarters address');
    }

    onProgress(70, 'CreatingCustomerContact');
    const customerContactResult = await client.mutate({
      mutation: CreateOrganizationAddressDocument,
      variables: {
        input: buildAddressInput(organizationId, 'CUSTOMER_CONTACT', customerContactAddress),
      },
    });

    if (!customerContactResult.data?.createOrganizationAddress?.organizationAddress?.id) {
      throw new Error('Failed to create customer contact address');
    }

    onProgress(100, 'SetupComplete');

    // Clear localStorage after successful completion
    clearSetupData();

    return { organizationId };
  } catch (error: unknown) {
    // Don't clear localStorage on error so user can retry
    const message =
      error instanceof Error ? error.message : 'An unexpected error occurred during setup';
    throw new Error(message, { cause: error });
  }
};
